<template>
  <div
    class="d-flex flex-row align-center ga-2"
    :class="inline ? 'w-auto' : 'w-100'"
  >
    <!-- The hint icon/menu in "label-append" must stay OUTSIDE the v-btn: nesting
         another interactive element (the menu activator) inside a native <button>
         is invalid HTML and unreliably swallows its own hover/click handling. -->
    <span
      class="switch-label-wrapper d-flex align-center overflow-hidden"
      :class="inline ? 'flex-0-1' : 'flex-1-1-0'"
    >
      <!-- variant=text for Vuetify chrome; overrides below undo VBtn defaults that
           block wrap (flex-shrink:0, nowrap content, fixed height) so long DE/FR
           labels still wrap and the switch stays on-screen. -->
      <v-btn
        class="switch-label overflow-hidden text-none text-left"
        :class="labelClassValue"
        density="compact"
        :disabled="disabled"
        :ripple="false"
        slim
        variant="text"
        @click="onLabelClick"
      >
        <span>{{ label }}</span>
      </v-btn>
      <span class="switch-label-append">
        <slot name="label-append"></slot>
      </span>
    </span>

    <v-switch
      :id="switchId"
      v-model="switchValue"
      class="ma-0 flex-shrink-0"
      :class="{ active: switchValue }"
      :data-test-id="dataTestId"
      density="compact"
      :disabled="disabled"
      hide-details
      @keydown.enter="emit('value-changed', !switchValue)"
      @update:model-value="onChange"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  // Shrink-to-fit: keeps the switch next to its label instead of stretching to
  // the container width and pushing the switch away from it. The label can still
  // shrink/wrap (flex: 0 1 auto) so long DE/FR labels don't overflow.
  inline: {
    type: Boolean,
    default: false
  },
  initialValue: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    required: true
  },
  labelClass: {
    type: String,
    default: null
  },
  switchId: {
    type: String,
    default: null
  },
  dataTestId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['value-changed']);

const switchValue = ref(props.initialValue);

const labelClassValue = computed(() => {
  const classes = [props.labelClass, props.disabled ? 'text-grey' : null];
  return classes.filter(Boolean).join(' ');
});

const onChange = (event) => {
  emit('value-changed', event);
};

const onLabelClick = () => {
  if (!props.disabled) {
    switchValue.value = !switchValue.value;
    onChange(switchValue.value);
  }
};

watch(
  () => props.initialValue,
  (newVal) => {
    switchValue.value = newVal;
  }
);
</script>

<style scoped lang="scss">
.switch-label-wrapper {
  min-width: 0;
}

.switch-label.v-btn {
  // VBtn ships flex-shrink:0 + fixed height + nowrap content — undo those so
  // the label can shrink/wrap beside the switch on narrow viewports.
  flex-shrink: 1 !important;
  height: auto !important;
  min-width: 0 !important;
  padding-inline: 0 !important;
  justify-content: flex-start;

  :deep(.v-btn__content) {
    display: block;
    width: 100%;
    height: auto;
    white-space: normal;
    text-align: left;
    line-height: inherit;
    opacity: 1;
  }

  // Label-as-control: no hover/focus wash (keeps it looking like plain text).
  :deep(.v-btn__overlay) {
    display: none;
  }
}

// Keep icon glued to the label text and optically centered on the cap-height /
// x-height of drawer-subhead (14px / 17px).
.switch-label-append {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  vertical-align: middle;
  margin-inline-start: 4px;
  line-height: 1;

  :deep(.v-icon) {
    margin: 0;
    line-height: 1;
  }
}

// Switch chrome / Vuetify internals — utilities can't target these.
:deep(.v-input--density-compact) {
  --v-input-control-height: 30px;
}

.v-switch.active :deep(.v-switch__track) {
  background-color: color-mix(
    in srgb,
    rgb(var(--v-theme-primary)) 25%,
    white
  ) !important;
}

.v-switch.active :deep(.v-switch__thumb) {
  background-color: rgb(var(--v-theme-primary)) !important;
}
</style>
